'use client'

import { AutoPlayConfig, HomeGamesList } from 'modules/constant'
import { cn } from 'modules/utils'
import Image from 'next/image'
import { useRef } from 'react'
import { Autoplay, EffectCoverflow, Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/navigation'
import 'swiper/css/autoplay'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper/types'
import Link from 'next/link'
import { Video } from 'modules/components'
import { motion } from 'framer-motion'
import { TrackerProvider, TrackExposure, TrackHomeSection } from 'modules/analysis'

export function GamesSwiper() {
  const swiperRef = useRef<SwiperClass | null>(null)
  const timeoutId = useRef<NodeJS.Timeout | null>(null)

  return (
    <div className="relative w-full">
      <TrackExposure config={{ sectionName: TrackHomeSection.GamesSlide, attr: { slideType: 'left' } }} withClick>
        <div
          className="swiper-button-prev"
          onClick={() => {
            if (timeoutId.current) clearTimeout(timeoutId.current)
            timeoutId.current = null
            swiperRef.current?.autoplay?.start()
            swiperRef.current?.slidePrev()
          }}
        />
      </TrackExposure>
      <TrackExposure config={{ sectionName: TrackHomeSection.GamesSlide, attr: { slideType: 'right' } }} withClick>
        <div
          className="swiper-button-next"
          onClick={() => {
            if (timeoutId.current) clearTimeout(timeoutId.current)
            timeoutId.current = null
            swiperRef.current?.autoplay?.start()
            swiperRef.current?.slideNext()
          }}
        />
      </TrackExposure>

      <Swiper
        onSwiper={(val) => (swiperRef.current = val)}
        effect="coverflow"
        centeredSlides
        slidesPerView="auto"
        loopAdditionalSlides={1}
        coverflowEffect={{
          rotate: 15, // 旋转角度（左右倾斜）
          stretch: 15, // 负值让两侧靠拢
          depth: 250, // 控制层叠深度
          modifier: 1,
          slideShadows: false,
        }}
        modules={[EffectCoverflow, Autoplay, Navigation]}
        className="section-content linear-mask-[to_right,transparent_0%,white_30%,white_70%,transparent_100%]"
        autoplay={{ ...AutoPlayConfig, pauseOnMouseEnter: false }}
        loop
        speed={1000}
      >
        {HomeGamesList.map(({ content, video, key, route }, index) => (
          <SwiperSlide
            key={key}
            className="rounded-4xl fix-swiper-3d w-1/4 min-w-[min(25%,285px)] max-w-[min(25%,380px)]"
          >
            {({ isActive, isNext, isPrev }) => (
              <div
                className={cn('rounded-4xl relative size-full cursor-pointer overflow-hidden', isActive && 'group')}
                onMouseEnter={() => {
                  if (isActive) {
                    if (timeoutId.current) {
                      clearTimeout(timeoutId.current)
                    }
                    timeoutId.current = null
                    swiperRef.current?.autoplay?.stop()
                  }
                }}
                onMouseLeave={() => {
                  if (isActive) {
                    timeoutId.current = setTimeout(() => {
                      swiperRef.current?.autoplay?.start()
                      swiperRef.current?.slideNext()
                    }, 2000)
                  }
                }}
              >
                <motion.div
                  className={cn('rounded-4xl absolute inset-0 size-full')}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isActive ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {(isActive || isNext || isPrev) && (
                    <Video
                      loop
                      replay={isActive}
                      src={video}
                      className="rounded-5xl size-full"
                      height={780}
                      width={380}
                    />
                  )}
                </motion.div>
                <Image
                  src={content}
                  alt={key}
                  className={cn('rounded-4xl size-full', isActive && 'invisible')}
                  height={780}
                  width={380}
                />
                <TrackerProvider
                  config={{
                    attr: {
                      gamesId: key,
                      gamesPosition: index,
                    },
                  }}
                >
                  <TrackExposure
                    config={{
                      sectionName: TrackHomeSection.Games,
                    }}
                  >
                    <div
                      className={cn(
                        isActive ? 'flex' : 'hidden',
                        'rounded-b-4xl absolute inset-0 bottom-0 size-full items-end justify-center group-hover:flex',
                        'bg-linear-[transparent_0%,transparent_60%,#000_90%] translate-z-24',
                      )}
                    >
                      <TrackExposure
                        config={{
                          sectionName: TrackHomeSection.GamesGo,
                        }}
                        withNavigate
                      >
                        <Link
                          href={route}
                          className="m-[20%] w-full max-w-60 text-2xl font-bold uppercase opacity-80 hover:opacity-100"
                        >
                          <button className="min-w-0! py-[0.5em]! w-full border border-white">Go</button>
                        </Link>
                      </TrackExposure>
                    </div>
                  </TrackExposure>
                </TrackerProvider>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
